import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Info } from "lucide-react";
import { CATEGORIES } from "../data/pravilnik";

const Row = ({ item, count, onChange }) => {
  const [open, setOpen] = useState(false);
  const subtotal = (Number(count) || 0) * item.points;
  const hasInfo = item.doc || item.note;
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700 leading-snug">
            {item.label}
            {hasInfo && (
              <button
                type="button"
                data-testid={`info-${item.id}`}
                onClick={() => setOpen((o) => !o)}
                aria-label="Tumačenje i potrebni dokazi"
                title="Tumačenje i potrebni dokazi"
                className={`inline-flex align-middle ml-1.5 transition-colors duration-200 ${open ? "text-blue-600" : "text-slate-400 hover:text-blue-600"}`}
              >
                <Info size={15} />
              </button>
            )}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">{item.points} {item.points === 1 ? "bod" : item.points < 5 ? "boda" : "bodova"} po jedinici</p>
        </div>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={99}
          value={count || ""}
          placeholder="0"
          data-testid={`activity-${item.id}`}
          onChange={(e) => onChange(item.id, e.target.value)}
          className="w-16 shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
        />
        <span
          className={`w-14 shrink-0 text-right text-sm font-semibold tabular-nums ${subtotal > 0 ? "text-blue-600" : "text-slate-300"}`}
        >
          {subtotal} bd
        </span>
      </div>
      {open && hasInfo && (
        <div
          className="mt-2 mr-[7.75rem] rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 space-y-1"
          data-testid={`info-panel-${item.id}`}
        >
          {item.doc && <p className="text-xs text-slate-600 leading-relaxed">{item.doc}</p>}
          {item.note && <p className="text-xs text-blue-800 leading-relaxed">{item.note}</p>}
        </div>
      )}
    </div>
  );
};

export const ActivityTable = ({ counts, setCounts, byCategory }) => {
  const onChange = (id, value) => {
    const n = Math.max(0, Math.min(99, Number(value) || 0));
    setCounts((c) => ({ ...c, [id]: value === "" ? "" : n }));
  };

  return (
    <section className="bg-white p-5 sm:p-6 rounded-xl shadow-sm border border-slate-200" data-testid="activity-table-panel">
      <h2 className="text-lg font-semibold text-slate-800 font-heading">Bodovna tablica aktivnosti</h2>
      <p className="text-sm text-slate-500 mt-1 mb-4">
        Unesite broj ostvarenih aktivnosti u razdoblju koje se vrednuje. Bodovi se zbrajaju automatski.
        Klikom na ikonu <Info size={13} className="inline align-[-2px] text-blue-600" /> uz aktivnost
        prikazuju se tumačenja MZOM-a i potrebni dokazi.
      </p>
      <Accordion type="multiple" className="space-y-3">
        {CATEGORIES.map((cat) => {
          const catPoints = byCategory[cat.id] || 0;
          return (
            <AccordionItem
              key={cat.id}
              value={cat.id}
              className="border border-slate-200 rounded-lg overflow-hidden !border-b"
            >
              <AccordionTrigger
                data-testid={`category-trigger-${cat.id}`}
                className="bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 hover:no-underline hover:bg-slate-100"
              >
                <span className="flex-1 text-left pr-3">
                  {cat.title}
                  {cat.mandatoryHint && (
                    <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wide text-blue-600 bg-blue-100 rounded-full px-2 py-0.5">
                      obvezna
                    </span>
                  )}
                </span>
                <span
                  data-testid={`category-points-${cat.id}`}
                  className={`text-sm font-bold tabular-nums mr-2 ${catPoints > 0 ? "text-blue-600" : "text-slate-400"}`}
                >
                  {catPoints} bd
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pt-1 pb-2">
                {cat.categoryNote && (
                  <div
                    className="mt-1 mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2"
                    data-testid={`category-note-${cat.id}`}
                  >
                    <p className="text-xs text-amber-900 leading-relaxed">{cat.categoryNote}</p>
                  </div>
                )}
                {cat.items.map((item) => (
                  <Row key={item.id} item={item} count={counts[item.id]} onChange={onChange} />
                ))}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </section>
  );
};
